import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FaHistory } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import { useCheckSessionHeartBeat } from "../API/ApiEnpoints";
import "./HrViewHistory.css";

const beAddress =
  window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

type PullHistoryItem = {
  org_uid?: string;
  start_datetime?: string;
  end_datetime?: string;
  portal_type?: string;
  pull_uid?: string;
  pull_status?: string;
  created_by?: string;
  message?: string;
  time_taken?: string;
};

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const HrViewHistory: React.FC = () => {
  useCheckSessionHeartBeat();
  const location = useLocation();

  const userUid = localStorage.getItem("user_uid") || "";
  const timezone = localStorage.getItem("timeZone") || "UTC";
  const initialPullUid =
    (location.state as { pullUid?: string } | null)?.pullUid || "";

  const [history, setHistory] = useState<PullHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchValue, setSearchValue] = useState(initialPullUid);
  const [searchInput, setSearchInput] = useState(initialPullUid);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const formatDateTime = (value?: string) => {
    if (!value) return "NA";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleString("en-IN", {
      timeZone: timezone,
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const resolveSearchParams = useMemo(() => {
    const trimmedValue = searchValue.trim();

    if (!trimmedValue) {
      return {
        pull_uid: undefined,
      };
    }

    return {
      pull_uid: trimmedValue,
    };
  }, [searchValue]);

  useEffect(() => {
    const fetchPullHistory = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await axios.get(`${beAddress}/api/get-pull-history`, {
          headers: {
            "user-uid": userUid,
          },
          withCredentials: true,
          params: {
            page: currentPage,
            page_size: pageSize,
            ...resolveSearchParams,
          },
        });

        const payload = response.data ?? {};
        const responseData = payload.data ?? payload;
        const nestedData =
          responseData && typeof responseData === "object" && "data" in responseData
            ? (responseData as { data?: unknown }).data
            : undefined;
        const recordsSource = nestedData ?? responseData;
        const records = Array.isArray(recordsSource)
          ? recordsSource
          : Array.isArray((recordsSource as { records?: PullHistoryItem[] })?.records)
            ? (recordsSource as { records: PullHistoryItem[] }).records
            : Array.isArray((recordsSource as { pull_history?: PullHistoryItem[] })?.pull_history)
              ? (recordsSource as { pull_history: PullHistoryItem[] }).pull_history
              : Array.isArray((recordsSource as { history?: PullHistoryItem[] })?.history)
                ? (recordsSource as { history: PullHistoryItem[] }).history
                : (recordsSource as PullHistoryItem)?.pull_uid
                  ? [recordsSource as PullHistoryItem]
                  : [];

        const totalCount =
          (responseData as { total_count?: number })?.total_count ??
          (responseData as { count?: number })?.count ??
          payload.total_count ??
          (currentPage === 1 && records.length < pageSize
            ? records.length
            : (currentPage - 1) * pageSize + records.length);

        setHistory(records);
        setTotalRows(totalCount);
      } catch (fetchError) {
        console.error("Error fetching pull history:", fetchError);
        setHistory([]);
        setTotalRows(0);
        setError("Failed to fetch pull history.");
      } finally {
        setLoading(false);
      }
    };

    fetchPullHistory();
  }, [currentPage, pageSize, resolveSearchParams, userUid]);

  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));

  const handleSearch = () => {
    setCurrentPage(1);
    setSearchValue(searchInput.trim());
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  return (
    <div className="hr-view-history-container">
      <div className="hr-view-history-header">
        <div>
          <h1>Pull History</h1>
          <p>Track each HR pull request, status, duration, and owner.</p>
        </div>
      </div>

      <div className="hr-view-history-toolbar">
        <div className="hr-view-history-search-group">
          <input
            type="text"
            placeholder="Search by pull UID"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="hr-view-history-search-box"
          />
          <button
            className="hr-view-history-search-button"
            onClick={handleSearch}
          >
            Search
          </button>
        </div>

        <div className="hr-view-history-page-size">
          <label htmlFor="page-size">Rows per page</label>
          <select
            id="page-size"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            {PAGE_SIZE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <p className="hr-history-message hr-history-error">{error}</p>}

      <div className="hr-history-table-wrapper">
        <table className="hr-history-table">
          <thead>
            <tr>
              <th>Pull UID</th>
              <th>Portal</th>
              <th>Status</th>
              <th>Started On</th>
              <th>Completed On</th>
              <th>Duration</th>
              <th>Created By</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="hr-history-message">
                  Loading pull history...
                </td>
              </tr>
            ) : history.length > 0 ? (
              history.map((item, index) => (
                <tr key={item.pull_uid || `${item.start_datetime}-${index}`}>
                  <td>{item.pull_uid || "NA"}</td>
                  <td>{item.portal_type || "NA"}</td>
                  <td>
                    <span
                      className={`hr-history-status ${
                        (item.pull_status || "").toLowerCase() === "completed"
                          ? "success"
                          : "pending"
                      }`}
                    >
                      {item.pull_status || "NA"}
                    </span>
                  </td>
                  <td>{formatDateTime(item.start_datetime)}</td>
                  <td>{formatDateTime(item.end_datetime)}</td>
                  <td>{item.time_taken || "NA"}</td>
                  <td>{item.created_by || "NA"}</td>
                  <td>{item.message || "NA"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9} className="hr-history-message">
                  <span className="hr-history-empty">
                    <FaHistory />
                    No pull history found.
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="hr-view-history-pagination-container">
        <button
          onClick={handlePreviousPage}
          disabled={currentPage === 1 || loading}
          className="hr-view-history-pagination-button"
        >
          Previous
        </button>
        <span className="hr-view-history-page-info">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={handleNextPage}
          disabled={loading || currentPage >= totalPages || history.length === 0}
          className="hr-view-history-pagination-button"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default HrViewHistory;
